"use client";

import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import useElementEvents from "@/hooks/eventing/useElementEvents";

interface CartIconProps {
  className?: string;
  shouldShowBadge: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

export default function CartIcon({
  className,
  shouldShowBadge,
  onClick,
  onMouseEnter,
}: CartIconProps) {
  // Element event tracking for cart icon
  const { elementViewed, elementClicked } = useElementEvents({
    webElement: {
      elementType: shouldShowBadge ? "cart" : "cart-intent-match",
      htmlId: "cart",
      location: "nav-bar",
      name: shouldShowBadge ? "full-cart" : "abandoned-cart-intent",
      text: shouldShowBadge ? "In your cart" : "Your cart is empty",
    },
  });

  // Track element viewed when component mounts or badge state changes
  useEffect(() => {
    elementViewed();
  }, [elementViewed, shouldShowBadge]);

  const handleClick = () => {
    // Track element clicked
    elementClicked();
    onClick();
  };

  const handleMouseEnter = () => {
    // Track element viewed on hover
    elementViewed();
    onMouseEnter();
  };

  return (
    <Button
      variant="ghost"
      className={`relative ${className}`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
    >
      <div className="relative w-6 h-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="22"
          viewBox="0 0 36 34"
          fill="none"
          className="block w-full h-full"
        >
          <path
            d="M10.7631 33.5C9.8376 33.5 9.0467 33.1683 8.39042 32.5214C7.73414 31.8745 7.39758 31.0949 7.39758 30.1826C7.39758 29.2703 7.73414 28.4907 8.39042 27.8438C9.0467 27.1969 9.8376 26.8651 10.7631 26.8651C11.6886 26.8651 12.4795 27.1969 13.1358 27.8438C13.7921 28.4907 14.1287 29.2703 14.1287 30.1826C14.1287 31.0949 13.7921 31.8745 13.1358 32.5214C12.4795 33.1683 11.6886 33.5 10.7631 33.5ZM27.5908 33.5C26.6653 33.5 25.8744 33.1683 25.2181 32.5214C24.5618 31.8745 24.2253 31.0949 24.2253 30.1826C24.2253 29.2703 24.5618 28.4907 25.2181 27.8438C25.8744 27.1969 26.6653 26.8651 27.5908 26.8651C28.5163 26.8651 29.3072 27.1969 29.9635 27.8438C30.6198 28.4907 30.9564 29.2703 30.9564 30.1826C30.9564 31.0949 30.6198 31.8745 29.9635 32.5214C29.3072 33.1683 28.5163 33.5 27.5908 33.5ZM0.666504 3.6429V0.325439H6.18599L13.3378 15.254H25.1171L31.68 3.6429H35.5167L28.1125 16.8298C27.8096 17.3772 27.3889 17.8085 26.8672 18.1236C26.3456 18.4388 25.7734 18.5881 25.1676 18.5881H12.631L10.78 21.9055H30.9732V25.223H10.7631C9.50105 25.223 8.54187 24.6756 7.88559 23.5974C7.22931 22.5192 7.21248 21.4245 7.81828 20.3131L10.09 16.2493L4.03204 3.6429H0.666504Z"
            fill="#00262B"
          />
        </svg>
        {shouldShowBadge && (
          <div className="absolute top-1 right-1.5 -translate-y-1/2 z-10 w-2.5 h-2.5 bg-red-500 rounded-full" />
        )}
      </div>
    </Button>
  );
}
